import React, { useCallback, useState } from 'react';
import { FormGroup, TextInput, Form, TextArea, InputGroup, Label, Button, InputGroupItem } from '@patternfly/react-core';
import CopyIcon from '@patternfly/react-icons/dist/js/icons/copy-icon';
import { format, formatISO } from 'date-fns';

import { LicenseStatus } from '../../store/license/license-status';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { appConfigSelectors } from '../../store/appConfig/appConfigSlice';
import { DeploymentMode } from '../../store/appConfig/appDeploymentInfoTypes';
import { unixTimestampToDate } from '../../util/date-convert';
import { getModelTypeLangKeyFromString } from '../../i18n';
import { ModelType } from '../../store/global-types/customization-types';

interface CurrentLicenseProps {
    licenseStatus: LicenseStatus,
}

const CurrentLicense = (props: CurrentLicenseProps) => {
    const { licenseStatus } = props;

    const { t } = useTranslation();
    const deploymentInfo = useSelector(appConfigSelectors.selectAppDeploymentInfo);

    const installationIdFieldRef = React.useRef<HTMLInputElement>(null);
    const [showCopiedToClipboardLabelStyle, setCopiedToClipboardLabelStyle] = useState('hide');

    const handleCopyToClipboardClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        if (!installationIdFieldRef || !installationIdFieldRef.current) {
            return;
        }

        installationIdFieldRef.current?.select();
        document.execCommand('copy');
        (event.target as HTMLButtonElement).focus();
        setCopiedToClipboardLabelStyle('hide');
        setTimeout(() => setCopiedToClipboardLabelStyle('fade-in-out'), 1);
    }, [installationIdFieldRef]);

    const licensedModels = !licenseStatus ? '' : (licenseStatus.modelNames?.length > 0 ? licenseStatus.modelNames.join(', ') : t('licensePage.allFeaturesAvailable'));
    const licensedProducts = !licenseStatus ? [] : licenseStatus.modelTypes;

    const hasUnlimitedUse = licenseStatus.unlimitedUseExpiryTime.valueOf() !== 0;
    // const unlimitedUseExpiryTime = hasUnlimitedUse ?  formatISO(licenseStatus.unlimitedUseExpiryTime, { representation: 'date' }) : 'No unlimited use';
    // const unlimitedUseExpiryTimePretty = hasUnlimitedUse ? `Unlimited use until ${format(licenseStatus.unlimitedUseExpiryTime, 'iiii MMMM do y')}` : 'No unlimited use';

    const { id } = licenseStatus;

    // convert timestamp to Date objects
    const creationTime = unixTimestampToDate(licenseStatus.creationTime);
    const expiryTime = unixTimestampToDate(licenseStatus.expiryTime);

    const handleLicensedProducts = (licensedProducts: ModelType[]) => {
        return licensedProducts.map(product => getModelTypeLangKeyFromString(product)).join(', ');
    }

    return (
        <Form isHorizontal>
            <FormGroup
                label={t('licensePage.licensedProducts')}
                fieldId={`products-name-${id}`}
            >
                <TextInput
                    value={handleLicensedProducts(licensedProducts)}
                    type="text" id={`product-name-${id}`} readOnlyVariant="default"
                    data-testid="licensed-products-field" />
            </FormGroup>
            <FormGroup
                label={t('licensePage.licenseCreatedOn')}
                fieldId={`created-readonly-${id}`}
                title={`This license was created on ${format(creationTime, 'iiii MMMM do y')}.`} >
                <TextInput
                    value={formatISO(creationTime, { representation: 'date' })}
                    type="text" id={`created-readonly-${id}`} readOnlyVariant="default"
                    data-testid="license-created-on-field" />
            </FormGroup>
            <FormGroup
                label={t('licensePage.licenseValidUntil')}
                fieldId={`expires-readonly-${id}`}
                title={`This license is valid until ${format(expiryTime, 'iiii MMMM do y')}.`} >
                <TextInput
                    value={formatISO(expiryTime, { representation: 'date' })}
                    type="text" id={`expires-readonly-${id}`} readOnlyVariant="default"
                    data-testid="license-valid-until-field" />
            </FormGroup>
            <FormGroup
                label={t('licensePage.quotaRemaining')}
                fieldId={`quota-readonly-${id}`}
                title={hasUnlimitedUse ? 'This license has unlimited use.' : `Quota remaining in this license: ${licenseStatus.quotaLeft} / total: ${licenseStatus.quota}`} >
                <TextInput value={hasUnlimitedUse ? t('licensePage.unlimitedUse') : `${licenseStatus.quotaLeft} / ${licenseStatus.quota}`}
                    type="text" id={`quota-readonly-${id}`} readOnlyVariant="default"
                    data-testid="license-quota-remaining-field" />
            </FormGroup>
            <FormGroup
                label={t('licensePage.licensedFeatures')}
                fieldId={`licensed-features-readonly-${id}`}
                title={`Licensed features: ${licensedModels}`} >
                <TextArea
                    value={licensedModels}
                    type="text"
                    id={`licensed-features-readonly-${id}`}
                    spellCheck="false"
                    resizeOrientation='vertical'
                    disabled
                    data-testid="licensed-features-field" />
            </FormGroup>
            <FormGroup
                label={t('licensePage.processedTasks')}
                fieldId={`processed-tasks-readonly-${id}`}
                title={`Processed tasks with this license: ${licenseStatus.processedTasks}`} >
                <TextInput value={licenseStatus.processedTasks} type="text" id={`processed-tasks-readonly-${id}`}
                    readOnlyVariant="default"
                    data-testid="license-processed-tasks-field" />
            </FormGroup>
            {deploymentInfo && (deploymentInfo.deploymentMode === DeploymentMode.Cockpit || deploymentInfo.deploymentMode === DeploymentMode.Local) && (
                <FormGroup
                    label="Installation ID"
                    fieldId={`installation-id-readonly-${id}`} >
                    <InputGroup>
                        <InputGroupItem isFill ><TextInput
                            ref={installationIdFieldRef}
                            value={licenseStatus.installationId}
                            type="text"

                            id={`installation-id-readonly-${id}`}
                            onFocus={() => installationIdFieldRef && installationIdFieldRef.current && installationIdFieldRef!.current.select()} readOnlyVariant="default"
                        /></InputGroupItem>

                        <InputGroupItem><span className="copied-to-clipboard-label-container">
                            <span
                                className={`copied-to-clipboard-label ${showCopiedToClipboardLabelStyle}`}>
                                <Label
                                    color="green"
                                    onClose={() => setCopiedToClipboardLabelStyle('quick-fade-out')}
                                >Copied to clipboard</Label>
                            </span>
                        </span></InputGroupItem>

                        <InputGroupItem><Button variant="control" title="Copy Installation ID to clipboard" onClick={handleCopyToClipboardClick}>
                            <CopyIcon />
                        </Button></InputGroupItem>
                    </InputGroup>
                </FormGroup>
            )}
        </Form>
    );
}

export default CurrentLicense;
